import type { EventMeta, EventProvider, GlobalContext } from './types.ts'

export interface DispatchOptions {
  providers: EventProvider[]
  globalContext: () => GlobalContext
  disabled: () => boolean
  /**
   * When this returns true, every dispatched event is logged to the console, including events that
   * `disabled()` suppresses.
   */
  debug?: () => boolean
  isDev?: boolean
}

const firedEvents = new Set<string>()

export function resetFiredEvents() {
  firedEvents.clear()
}

export function dispatch(
  event: EventMeta,
  data: Record<string, unknown>,
  options: DispatchOptions,
): void {
  // The dedup gate runs first so a deduplicated event is logged, and dispatched, at most once.
  if (event.deduplicate && firedEvents.has(event.id)) return
  if (event.deduplicate) firedEvents.add(event.id)

  if (options.debug?.()) {
    // eslint-disable-next-line no-console
    console.log(`[event-library:debug] "${event.name}"`, {
      id: event.id,
      suppressed: options.disabled() ? 'disabled' : null,
      backends: Object.keys(event.backends),
      data,
    })
  }

  if (options.disabled()) return

  const safeData = JSON.parse(JSON.stringify(data))
  const context = options.globalContext()

  for (const provider of options.providers) {
    try {
      provider.send(event, safeData, context)
    } catch (error) {
      if (options.isDev) {
        console.error(
          `[event-library] ${provider.name} failed on "${event.name}":`,
          error,
        )
      }
    }
  }
}
